import { useEffect, useState } from "react";
import Paper from "@material-ui/core/Paper";
import { makeStyles } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import { DatePicker } from "@material-ui/pickers";
import moment from "moment";
import {
  ArgumentAxis,
  ValueAxis,
  Chart,
  LineSeries,
  Tooltip,
} from "@devexpress/dx-react-chart-material-ui";
import { EventTracker } from "@devexpress/dx-react-chart";
import Alert from "@material-ui/lab/Alert";
import { useIntl, FormattedMessage } from "react-intl";

import { TransactionItem } from "../../../../components/transaction_item/TransactionItem";
import {
  YYYY_MM_DD_FORMAT,
  MMM_DD_FORMAT,
  YYYY_MM_DD_HH_MM_FORMAT,
} from "../../../../settings/constants";
import { BranchManagerService } from "../../../../services/BranchManagerService";
import { TransactionRefundItem } from "../../../../components/transaction_item/TransactionRefundItem";

interface RefundReportTabProps {
  branch: number;
}

const useStyles = makeStyles((theme) => ({
  main: {
    minHeight: "75vh",
    overflow: "hidden",
    width: "100%",
    flexGrow: 1,
    paddingLeft: 10,
    paddingRight: 10,
    paddingTop: 20,
    paddingBottom: 20,
  },
  branchLocationTitle: {
    fontSize: 16,
    fontWeight: "bold",
  },
  branchLocation: {
    fontSize: 24,
  },
  employeeButton: {
    textTransform: "none",
    margin: 5,
  },
  sumRefunds: {
    display: "inline-block",
    fontSize: 20,
    fontWeight: "bold",
    color: "#36B516",
    padding: 5,
  },
  from:{
    //marginLeft: 10,
  },
  blockPicker:{
    padding: 20,
  },
  title: {
    display: "inline-block",
    fontSize: 24,
    padding: 5,
  },
}));

export const RefundReportTab = (props: RefundReportTabProps) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { branch } = props;
  const allEmployees: number = 0;
  const [branchLocation, setBranchLocation] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(allEmployees);
  const [branchEmployees, setBranchEmployees] = useState([]);
  const [selectedDateFrom, setSelectedDateFrom] = useState(moment());
  const [selectedDateTo, setSelectedDateTo] = useState(moment());
  const [turnover, setTurnover] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [currentBranch, setCurrentBranch] = useState(branch);
  const [sumRefunds, setSumRefunds] = useState(0);
  const [curr, setCurr] = useState();

  useEffect(() => {
    async function loadResume() {
      setLoading(true);
      setError(false);
      setTurnover([]);
      setTransactions([]);
      if (currentBranch !== branch) {
        setSelectedEmployee(allEmployees);
        setCurrentBranch(branch);
        return;
      }
      try {
        const resume = await BranchManagerService.resumeRefund({
          branch,
          employee: selectedEmployee,
          date_from: selectedDateFrom.format(YYYY_MM_DD_FORMAT),
          date_to: selectedDateTo.format(YYYY_MM_DD_FORMAT),
        });
        if (resume.isSuccess && resume.data.code === 100) {
          const responseData = JSON.parse(JSON.stringify(resume.data.data));
          let employees = responseData.employees;
          employees.unshift({
            name: formatMessage({ id: "sharedScreen.allEmployees" }),
            id: allEmployees,
          });
          let turnover = responseData.turnover;
          let transactions = responseData.transactions;
          let branchLocation = responseData.location;
          let sumRefunds = responseData.sumRefunds;
          let curren = responseData.devise;
          setBranchLocation(branchLocation);
          setBranchEmployees(employees);
          setTurnover(
            turnover.map((entry) => ({
              day: moment(entry.date, YYYY_MM_DD_FORMAT).format(MMM_DD_FORMAT),
              value: entry.amount,
            }))
          );
          setTransactions(
            transactions
              .map((entry) => ({
                ...entry,
                date: moment(entry.date, YYYY_MM_DD_HH_MM_FORMAT),
              }))
              .sort((left, right) => (left.date.isAfter(right.date) ? +1 : -1))
          );
          setSumRefunds(sumRefunds);
          setCurr(curren);
        } else {
          setError(true);
        }
      } catch (_) {
        setError(true);
      }
      setLoading(false);
    }
    loadResume();
  }, [selectedEmployee, selectedDateFrom, selectedDateTo, formatMessage, branch, currentBranch]);
  console.log(sumRefunds);
  return (
    <Paper className={classes.main} elevation={3}>
      <Grid container spacing={3}>
        <Grid container direction="column" item xs={12}>
          <Typography
            className={classes.branchLocationTitle}
            variant="h4"
            component="h4"
          >
            <FormattedMessage id="branchManagerScreen.branchLocation" />:
          </Typography>
          <Typography
            className={classes.branchLocation}
            variant="h3"
            component="h3"
          >
            {branchLocation}
          </Typography>
        </Grid>
        <Grid container direction="row" alignItems="center" item xs={12}>
          {branchEmployees.map((employee) => (
            <Button
              color="secondary"
              variant={
                employee.id === selectedEmployee ? "contained" : "outlined"
              }
              className={classes.employeeButton}
              onClick={() => setSelectedEmployee(employee.id)}
            >
              {employee.name}
            </Button>
          ))}
          {loading && <CircularProgress size={30} />}
          {error && (
            <Alert
              severity="error"
              action={
                <Button
                  onClick={() => setSelectedDateFrom(moment())}
                  color="inherit"
                  size="small"
                >
                  <FormattedMessage id="sharedScreen.loadAgain" />
                </Button>
              }
            >
              <FormattedMessage id="sharedScreen.thereWasAProblemLoadingDatas" />
            </Alert>
          )}
        </Grid>
        <Grid item xs={6}>
          <Typography className={classes.title} variant="h4" component="h4">
            <FormattedMessage id="sharedScreen.grossSale" />
          </Typography>
        </Grid>
        <Grid container justify="flex-end" item xs={6} >
          <Grid item xs={4} sm={2} lg={3} className={classes.blockPicker}>
            <FormattedMessage id="sharedScreen.dateFrom" />
            <DatePicker
              className={classes.from}
              views={["year", "month", "date"]}
              //maxDate={new Date()}
              value={selectedDateFrom}
              onChange={(newValue) => {
                setSelectedDateFrom(newValue);}}
            />
          </Grid>
          <Grid item xs={4} sm={2} lg={3} className={classes.blockPicker}>
            <FormattedMessage id="sharedScreen.dateTo" />
            <DatePicker
              className={classes.from}
              views={["year", "month", "date"]}
              //maxDate={new Date()}
              value={selectedDateTo}
              onChange={(newValue) => {
                setSelectedDateTo(newValue);}}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Paper elevation={3}>
            <Chart data={turnover} height={250}>
              <ArgumentAxis />
              <ValueAxis />

              <LineSeries
                name="turnover"
                valueField="value"
                argumentField="day"
              />
              <EventTracker />
              <Tooltip />
            </Chart>
          </Paper>
        </Grid>
        <Grid item xs={12} className={classes.sumRefunds} >
          <Typography className={classes.title} variant="h4" component="h4">
            <FormattedMessage id="sharedScreen.sumRefunds" /> 
          </Typography>
          <Typography className={classes.sumRefunds}>
          {curr} {sumRefunds}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography className={classes.title} variant="h4" component="h4">
            <FormattedMessage id="sharedScreen.refunds" />
          </Typography>
        </Grid>
        {transactions.map((transaction) => (
          <TransactionRefundItem {...transaction} />
        ))}
      </Grid>
    </Paper>
  );
};

export default RefundReportTab;
